import { Box } from '@ace-clublabs/shared-component-library';

import { useCallback, useEffect, useRef } from 'react';

import { useNavigate } from 'react-router-dom';
import ContactInfoForm, {
  type ContactInfoFields,
} from '@/components/shared/ContactInfoForm';
import { useAppointmentCreation } from '@/features/confirmation/api/useAppointmentCreation';
import useLandingPageRedirection from '@/hooks/useLandingPageRedirection';
import useProgress from '@/hooks/useProgress';
import useSearchedDestinationLabel from '@/hooks/useSearchedDestinationLabel';
import { Page } from '@/lib/tagging';
import useTagging from '@/lib/tagging/useTagging';
import { selectTrvEndLocationForTagging } from '@/lib/tagging/utils';
import useAppointmentStore, {
  isInBranchAppointment as isInBranchAppointmentSelected,
  isOnlineAppointment as isOnlineAppointmentSelected,
} from '@/store/AppointmentStore';
import useSessionStore from '@/store/SessionStore';

const CONTACT_INFO_FIELDS: ContactInfoFields[] = [
  'firstName',
  'lastName',
  'zipCode',
  'communicationPreference',
  'phoneNumber',
  'email',
  'expectedTravelTime',
  'typeOfTrip',
  'additionalInformation',
];

const ContactInfo = () => {
  const hasTagged = useRef(false);

  useProgress(80);

  const { contactInfo, patchContactInfo } = useAppointmentStore();
  const trvEndLocation = useAppointmentStore(selectTrvEndLocationForTagging);
  const searchedDestinationLabel = useSearchedDestinationLabel();

  const { sessionSource, sessionId, flowType } = useSessionStore();

  const isInBranchAppointment = useAppointmentStore(
    isInBranchAppointmentSelected,
  );
  const isOnlineAppointment = useAppointmentStore(isOnlineAppointmentSelected);

  const tagging = useTagging();

  useLandingPageRedirection();

  const navigate = useNavigate();

  useEffect(() => {
    if (!hasTagged.current) {
      const taggingData = {
        page: Page.contactInfo,
        sessionid: sessionId,
        flow: flowType,
        trv_end_location: trvEndLocation,
        comm_pref: isOnlineAppointment
          ? 'phone'
          : isInBranchAppointment
            ? 'in person'
            : undefined,
      };

      tagging.tag('view', sessionSource, taggingData);

      hasTagged.current = true;
    }
  }, [
    tagging.tag,
    sessionSource,
    sessionId,
    flowType,
    trvEndLocation,
    isOnlineAppointment,
    isInBranchAppointment,
  ]);

  const { handleStartAppointmentCreation } = useAppointmentCreation();

  const handleSubmit = useCallback(() => {
    handleStartAppointmentCreation();
    navigate('/confirmation', { replace: true });
  }, [handleStartAppointmentCreation, navigate]);

  return (
    <Box
      sx={[
        {
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          backgroundColor: 'background.default',
        },
      ]}
    >
      <ContactInfoForm
        formHeader='Almost ready to start planning'
        subHeader={
          searchedDestinationLabel
            ? `Share a few details so your ${searchedDestinationLabel} advisor can prep before your appointment.`
            : 'Share a few details so your advisor can prep before your appointment.'
        }
        fields={CONTACT_INFO_FIELDS}
        formValues={contactInfo}
        updateField={patchContactInfo}
        onSubmit={handleSubmit}
        continueButtonLabel='Book now'
      />
    </Box>
  );
};

export default ContactInfo;
